import find from 'lodash/find';
import React, { useEffect, useMemo, useRef } from 'react';

import { useScoreTypes } from './contexts/scoreTypes';

const TWO = 2;
const CIRCLE_SIZE = 30;
const RADIUS = 11;
const ARC_WIDTH = 8;
const FONT_SIZE = 12;
const CY_OFFSET_RATIO = 0.65;
const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

const drawMark = ({
  ctx,
  cx,
  cy,
  radius,
  arcWidth,
  fontSize,
  value,
  color,
}: {
  ctx: CanvasRenderingContext2D;
  cx: number;
  cy: number;
  radius: number;
  arcWidth: number;
  fontSize: number;
  value: number;
  color: string;
}) => {
  const tot = 100;
  const accum = 0;
  const PI = Math.PI;
  const PI2 = PI * TWO;
  const offset = -PI / TWO;
  const roundColor = !isDarkMode ? '#eeeeee' : '#333333';

  ctx.lineWidth = arcWidth;

  ctx.beginPath();
  ctx.arc(cx, cy, radius, offset + PI2 * (accum / tot), offset + PI2 * ((accum + value) / tot));
  ctx.strokeStyle = roundColor;
  ctx.stroke();

  const innerRadius = radius;
  ctx.beginPath();
  ctx.arc(cx, cy, innerRadius, 0, PI2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillStyle = 'white';
  ctx.font = 'bold ' + fontSize + 'px Roboto, Helvetica Neue';
  ctx.fillText(`${value}`, cx, cy + innerRadius * CY_OFFSET_RATIO);
};

const containerSize = 150;
const cx = containerSize / TWO;
const cy = containerSize / TWO;
const radius = (containerSize / CIRCLE_SIZE) * RADIUS;
const arcWidth = (containerSize / CIRCLE_SIZE) * ARC_WIDTH;
const fontSize = (containerSize / CIRCLE_SIZE) * FONT_SIZE;

const multiplier = containerSize / CIRCLE_SIZE;
const top = ((containerSize / multiplier) * (multiplier - 1)) / (-1 * TWO);
const left = top;
export const FundamentalScoreMark = ({ score, scoreType }: { score: number; scoreType: string }) => {
  const scoreTypes = useScoreTypes();
  const canvasRef = useRef(null);
  const fundamentalScoreTypes = useMemo(() => {
    return find(scoreTypes, (scoreType) => {
      return scoreType?.diagnosisType === 'FUNDAMENTAL';
    });
  }, [scoreTypes]);

  const color = useMemo(() => {
    return find(fundamentalScoreTypes?.scoreTypes, (types) => {
      return types?.key === scoreType;
    })?.colorCode;
  }, [scoreType, fundamentalScoreTypes]);

  useEffect(() => {
    if (canvasRef.current && score) {
      drawMark({ ctx: canvasRef.current.getContext('2d'), cx, cy, radius, arcWidth, fontSize, value: score, color });
    }
  }, [score, color]);

  return (
    <canvas
      ref={canvasRef}
      width={containerSize}
      height={containerSize}
      style={{ transform: `scale(${1 / multiplier})`, position: 'absolute', top, left }}
    />
  );
};
